import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { useSession } from "./SessionProvider";
import { sourcedKey, useReadSource, viewRows, type ReadSource } from "../data/readSource";

/**
 * Who the server says this session is, and what it says the session may do.
 *
 * plan.md section 8: tenant and role are derived from server-controlled membership data, never
 * from editable user metadata, and a hidden control is never access control. So nothing in this
 * file reads `user_metadata`, and nothing it returns is treated as permission — it decides what
 * the interface *offers*, while the gateway decides what actually happens.
 *
 * `portalAccessState` is the server's own word on whether writing is open, read from
 * `v_current_management`. The interface follows it rather than inferring a state from the last
 * error it happened to see.
 */

export type PortalAccessState = "write" | "read" | "none";
export type TenantRole = "client_authorised_contact" | "client_member";

export interface PortalIdentity {
  userId: string;
  firstName: string | null;
  displayName: string | null;
  tenantId: string | null;
  tenantDisplayName: string | null;
  role: TenantRole | null;
  isPlatformAdmin: boolean;
  portalAccessState: PortalAccessState;
  managementStatus: string | null;
  managementStartedOn: string | null;
  readOnlyUntil: string | null;
}

export const portalIdentityKey = ["portal", "identity"] as const;

/**
 * A Supabase result whose error is not allowed to become a fact about the person.
 *
 * Every read below used to be destructured as `{ data }` alone, dropping `error`, and each dropped
 * error turned a failure into a confident answer about who the caller is:
 *
 *   * the profile select failing meant a person with no name, and the shell rendered that;
 *   * `is_platform_admin` failing meant `admin === true` was false, so an admin silently became a
 *     client and every admin destination disappeared from their own portal;
 *   * the tenants select failing meant no tenant, and the overview renders that as
 *     "Ingen aktiv åtkomst", which is a denial screen shown for a transient network fault;
 *   * `current_tenant_role` or `v_current_management` failing meant `accessState` fell to "none",
 *     which read-locks the composer and tells a paying client their management has ended.
 *
 * This is the same rule the request thread and the chat follow after 2026-09-02: an absence is
 * rendered as an absence, and a failure is rendered as a failure. Neither is ever rendered as the
 * other, and neither is ever rendered as a person.
 *
 * A missing row stays a success on purpose. `maybeSingle()` answers `{ data: null, error: null }`
 * for zero rows, and "this person has no profile yet" is a true statement about a freshly
 * provisioned client. What is not true is "this person has no profile" when the request failed.
 */
function orThrow<T>(
  result: { data: T; error: { message: string; code?: string } | null },
  what: string
): T {
  if (result.error) {
    throw new Error(
      `PORTAL_IDENTITY_READ_FAILED: ${what} could not be read. ${result.error.message}`,
      { cause: result.error }
    );
  }

  return result.data;
}

export async function fetchPortalIdentity(): Promise<PortalIdentity> {
  const supabase = getSupabaseBrowserClient();

  // The user id comes from an RPC rather than from the decoded token, so the value the interface
  // renders is the one the database resolved from the same JWT it will authorise writes with.
  const { data: userId, error: userError } = await supabase.rpc("current_user_id");
  if (userError) {
    throw userError;
  }
  if (!userId) {
    throw new Error("PORTAL_UNAUTHENTICATED");
  }

  const [profileResult, tenantsResult, adminResult] = await Promise.all([
    supabase
      .from("profiles")
      .select("id, first_name, display_name")
      .eq("id", userId)
      .maybeSingle(),
    supabase.from("tenants").select("id, display_name").order("display_name"),
    supabase.rpc("is_platform_admin")
  ]);

  const profile = orThrow(profileResult, "your profile");
  const tenants = orThrow(tenantsResult, "your client accounts");
  const admin = orThrow(adminResult, "your administrator status");

  // RLS returns exactly the tenants this session may read. A client holds one; the empty case is a
  // real state (membership suspended, or management ended) and the shell renders it as such.
  const tenant = tenants?.[0] ?? null;

  let role: TenantRole | null = null;
  let accessState: PortalAccessState = "none";
  let managementStatus: string | null = null;
  let managementStartedOn: string | null = null;
  let readOnlyUntil: string | null = null;

  if (tenant) {
    const [roleResult, managementResult] = await Promise.all([
      supabase.rpc("current_tenant_role", { p_tenant_id: tenant.id }),
      supabase
        .from("v_current_management")
        .select("status, portal_access_state, started_on, read_only_until")
        .eq("tenant_id", tenant.id)
        .maybeSingle()
    ]);

    const tenantRole = orThrow(roleResult, "your role");
    const management = orThrow(managementResult, "your management agreement");

    role = (tenantRole as TenantRole | null) ?? null;
    accessState = (management?.portal_access_state as PortalAccessState | null) ?? "none";
    managementStatus = management?.status ?? null;
    managementStartedOn = management?.started_on ?? null;
    readOnlyUntil = management?.read_only_until ?? null;
  }

  return {
    userId,
    firstName: profile?.first_name ?? null,
    displayName: profile?.display_name ?? null,
    tenantId: tenant?.id ?? null,
    tenantDisplayName: tenant?.display_name ?? null,
    role,
    isPlatformAdmin: admin === true,
    portalAccessState: accessState,
    managementStatus,
    managementStartedOn,
    readOnlyUntil
  };
}

/**
 * A user id that is nobody's, for the view (build round 41). The view shows a role, not a person, so
 * no message, comment or file in it is "yours": each is named as the client would see it from
 * someone else, and Petrovic's own messages stay Petrovic's.
 */
export const VIEW_USER_ID = "00000000-0000-4000-8000-000000000000";

/**
 * The identity a client screen sees inside "Visa som kund": the client's name, the role the view
 * shows, the person the banner names, and the client's real access state from
 * `public.admin_as_client_management`, so a paused or read-only portal reads as it does to the client.
 */
async function fetchViewIdentity(source: Extract<ReadSource, { kind: "admin" }>): Promise<PortalIdentity> {
  const management = (
    await viewRows<{ status: string; portal_access_state: PortalAccessState; started_on: string | null; read_only_until: string | null }>(
      "admin_as_client_management",
      { p_tenant_id: source.tenantId }
    )
  )[0];

  return {
    userId: VIEW_USER_ID,
    firstName: source.personName?.split(" ")[0] ?? null,
    displayName: source.personName,
    tenantId: source.tenantId,
    tenantDisplayName: source.tenantName,
    role: source.role,
    isPlatformAdmin: false,
    portalAccessState: management?.portal_access_state ?? "none",
    managementStatus: management?.status ?? null,
    managementStartedOn: management?.started_on ?? null,
    readOnlyUntil: management?.read_only_until ?? null
  };
}

export function usePortalIdentity(): UseQueryResult<PortalIdentity> {
  const { status } = useSession();
  const source = useReadSource();

  return useQuery({
    queryKey: sourcedKey(source, portalIdentityKey),
    queryFn: source.kind === "admin" ? () => fetchViewIdentity(source) : fetchPortalIdentity,
    enabled: status === "signed-in",
    // Re-derived rather than remembered: a suspended membership or a paused management period must
    // reach the interface without a reload.
    staleTime: 30_000,
    refetchOnWindowFocus: true
  });
}

/** True only when the server says this tenant may write. Never inferred from the last error. */
export function canWrite(identity: PortalIdentity | undefined): boolean {
  return identity?.portalAccessState === "write";
}

/**
 * A person's name for display, with "still loading" and "there is no name" kept apart.
 *
 * They were not kept apart, and on 2026-09-02 a real client saw the result. `public.profiles` had no
 * row at all for them, because provisioning writes `public.contacts` and nothing created a profile.
 * The rail rendered `person ?? "Laddar"` and showed **"Laddar"** as their name, forever. The overview
 * rendered `firstName ?? displayName ?? "där"` and greeted them as **"Välkommen, där"**.
 *
 * Two separate faults, and the second one is the one that generalises. A permanent state wearing a
 * temporary state's clothes is worse than an ugly empty string: it tells the reader to wait for
 * something that is never going to arrive, and it tells whoever is looking at the screen over their
 * shoulder that the page is merely slow.
 *
 * `||` rather than `??` on purpose. `??` only catches null and undefined, so a profile row that
 * exists with `display_name = ''` walks straight through it and renders as nothing at all. An empty
 * name and an absent name are the same fact to a reader and are treated as one fact here.
 */
export type PersonName =
  | { kind: "loading" }
  | { kind: "named"; display: string; first: string }
  | { kind: "unnamed" };

export function personName(query: {
  data?: PortalIdentity;
  isPending: boolean;
}): PersonName {
  if (query.isPending || !query.data) {
    return { kind: "loading" };
  }

  const display = query.data.displayName?.trim() || "";
  const first = query.data.firstName?.trim() || "";

  if (!display && !first) {
    return { kind: "unnamed" };
  }

  return { kind: "named", display: display || first, first: first || display };
}
